import { useCallback, useEffect, useRef, useState } from 'react'

import { Card } from './Card'
import styles from './CardFan.module.css'
import { useGestureEntropy } from './useGestureEntropy'

/** Градусов между соседними картами. */
const STEP_DEG = 7
/** Сколько градусов даёт один пиксель движения пальца. */
const DEG_PER_PX = 0.32
/** Затухание инерции за кадр. */
const FRICTION = 0.94
/** Ниже этой скорости считаем, что веер остановился. */
const STOP_DEG = 0.02
/** Радиус дуги в ширинах карты. Центр дуги — ниже контейнера. */
const ARC = 1.7

export interface CardFanProps {
  /** Сколько карт в веере. Обычно вся колода. */
  count: number
  back: string
  /** Сколько карт нужно выбрать. Дальше веер перестаёт отвечать на выбор. */
  picks?: number
  onPicksChange?: (indexes: number[]) => void
  /**
   * Жест закончился и накопил достаточно движения.
   * Отдаёт дайджест траектории — приложение решает, что с ним делать.
   */
  onEntropy?: (digest: string) => void
  disabled?: boolean
}

/**
 * Веер карт — и способ выбрать карту, и источник энтропии.
 *
 * В старой версии здесь была карусель, но выбор был бутафорией: карты уже
 * выбрал сервер до того, как фронт что-то отправил. Здесь наоборот — движение
 * пальца определяет порядок, и это можно проверить математически.
 *
 * Физика сознательно простая: следование один к одному без сглаживания,
 * затухание по кадрам, доводка до ближайшего слота. Пружины и упругость
 * здесь неуместны — карты тяжёлые.
 */
export function CardFan({
  count,
  back,
  picks = 3,
  onPicksChange,
  onEntropy,
  disabled = false,
}: CardFanProps) {
  const [angle, setAngle] = useState(0)
  const [selected, setSelected] = useState<number[]>([])
  const [dragging, setDragging] = useState(false)

  const entropy = useGestureEntropy()
  const velocity = useRef(0)
  const lastX = useRef(0)
  const frame = useRef(0)

  const clamp = useCallback(
    (deg: number) => Math.max(-(count - 1) * STEP_DEG, Math.min(0, deg)),
    [count],
  )

  /** Инерция и доводка до ближайшего слота. */
  const settle = useCallback(() => {
    cancelAnimationFrame(frame.current)

    const tick = () => {
      velocity.current *= FRICTION

      if (Math.abs(velocity.current) < STOP_DEG) {
        // Доводка: веер всегда останавливается на карте, а не между картами.
        setAngle((a) => clamp(Math.round(a / STEP_DEG) * STEP_DEG))
        return
      }

      setAngle((a) => clamp(a + velocity.current))
      frame.current = requestAnimationFrame(tick)
    }

    frame.current = requestAnimationFrame(tick)
  }, [clamp])

  useEffect(() => () => cancelAnimationFrame(frame.current), [])

  const onPointerDown = (e: React.PointerEvent) => {
    if (disabled) return
    cancelAnimationFrame(frame.current)
    e.currentTarget.setPointerCapture(e.pointerId)
    lastX.current = e.clientX
    velocity.current = 0
    setDragging(true)
    entropy.record(e.clientX, e.clientY)
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging || disabled) return

    const dx = e.clientX - lastX.current
    lastX.current = e.clientX

    const delta = dx * DEG_PER_PX
    velocity.current = delta
    setAngle((a) => clamp(a + delta))
    entropy.record(e.clientX, e.clientY)
  }

  const onPointerUp = async (e: React.PointerEvent) => {
    if (!dragging) return
    setDragging(false)
    e.currentTarget.releasePointerCapture(e.pointerId)
    settle()

    // Меньше десятка точек — это тап, а не тасовка. Такой жест не считаем.
    if (onEntropy && entropy.count() >= 10) {
      onEntropy(await entropy.digest())
    }
  }

  const toggle = (i: number) => {
    if (disabled) return

    setSelected((prev) => {
      const next = prev.includes(i)
        ? prev.filter((x) => x !== i)
        : prev.length >= picks
          ? prev
          : [...prev, i]

      if (next !== prev) onPicksChange?.(next)
      return next
    })
  }

  const done = selected.length >= picks

  return (
    <div className={styles.wrap}>
      <div
        className={[styles.fan, dragging ? styles.dragging : null].filter(Boolean).join(' ')}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        role="listbox"
        aria-label={`Выбери ${picks} карты из ${count}`}
        aria-orientation="horizontal"
      >
        {Array.from({ length: count }, (_, i) => {
          const deg = angle + i * STEP_DEG
          // За пределами видимого сектора карты не рисуем: сорок карт в DOM
          // ради невидимого хвоста — это дёрганый веер на слабом телефоне.
          if (Math.abs(deg) > 34) return null

          const isSelected = selected.includes(i)

          return (
            <div
              key={i}
              className={styles.slot}
              style={{
                transform: `rotate(${deg}deg) translateY(calc(var(--card-w-lg) * ${ARC}))`,
                zIndex: Math.round(100 - Math.abs(deg)),
              }}
            >
              <Card
                back={back}
                label={isSelected ? `Карта ${i + 1}, выбрана` : `Карта ${i + 1}`}
                size="lg"
                selected={isSelected}
                dimmed={done && !isSelected}
                onSelect={() => toggle(i)}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
